import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, getMe } from "@/lib/api";
import { removeAuthenticatedFromCache, unscopedKey } from "@/lib/queryClient";
import { revokeAllThumbnails } from "@/features/coach-review/hooks/useThumbnail";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";

/** TanStack Query wrapper around `GET /api/me`.
 *
 * Side effects (in `useEffect` so React Strict Mode dev double-mount
 * stays idempotent):
 *   - On success: copy user + profile into the auth store, and
 *     active_scope + selection_required into the scope store. These
 *     stores are the synchronous read sources for non-React code
 *     (the api fetch wrapper, query-key builders).
 *   - On 401: mark auth store as anonymous AND clear the scope store
 *     AND REMOVE every authenticated/private query from the cache (not
 *     invalidate). `removeQueries` evicts entries entirely so the next
 *     subscriber goes into `pending` and has to wait for a fresh fetch
 *     — vs `invalidateQueries`, which marks data stale but keeps it in
 *     the cache, allowing a synchronous read of the previous user's data
 *     across the auth boundary. Privacy isolation needs removal;
 *     freshness needs invalidation. See `removeAuthenticatedFromCache`
 *     in `lib/queryClient.ts` for the cache primitive.
 *   - On any other error: leave the stores alone. The error bubbles
 *     to the caller via `query.error`.
 *
 * Don't read user/profile from this hook in components — read from
 * `useAuthStore(...)` and `useScopeStore(...)` so a component
 * doesn't need to know whether the cache is warm.
 */
export function useMe() {
  const applyMe = useAuthStore((s) => s.applyMePayload);
  const applyAnon = useAuthStore((s) => s.applyAnonymous);
  const applyScope = useScopeStore((s) => s.applyMePayload);
  const clearScope = useScopeStore((s) => s.clear);

  const query = useQuery({
    queryKey: unscopedKey("me"),
    queryFn: getMe,
    retry: false, // /me's 401 is informational, not a network error
    staleTime: 5 * 60_000, // user identity doesn't change often
  });

  useEffect(() => {
    if (query.data) {
      applyMe(query.data);
      applyScope(query.data);
    }
    if (query.error instanceof ApiError && query.error.status === 401) {
      applyAnon();
      clearScope();
      // Privacy boundary, not freshness boundary — remove every cached
      // authenticated/private query so the next observer goes into
      // `pending` state and has to wait for the new fetch. Keeping
      // private unscoped admin/account data here would let a later user
      // in the same tab momentarily see the previous user's data.
      removeAuthenticatedFromCache();
      // Thumbnails live outside the TanStack cache — they're object
      // URLs in a module-level Map. Revoke them all on the same
      // boundary so a prior session's note/clip JPEGs don't outlive
      // their visibility context.
      revokeAllThumbnails();
    }
  }, [query.data, query.error, applyMe, applyAnon, applyScope, clearScope]);

  return query;
}
